import React from 'react';

import { Root } from '@atlaskit/navigation-system/layout/root';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightManager } from '@atlaskit/onboarding';
import { usePanelContext } from '@atlassian/entry-point-panel/use-panel-context';
import { defineMessage, useIntl } from 'react-intl';

import { DISMISS_SPOTLIGHT_TOUR_EVENT_NAME } from '@townsquare/config/constants';
import { GoalCapabilitiesProvider } from '@townsquare/goal-capabilities';
import { GriffinTopNavigation } from '@townsquare/griffin-navigation';
import { GlobalAppShortcutsProvider } from '@townsquare/griffin-navigation/global-navigation-context';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { NavPlatformSideNav } from '@townsquare/nav-platform-integration/NavPlatformSideNav';
import { AppHomeWrapper } from '@townsquare/navigation/app-home-wrapper';
import { IntroPopup } from '@townsquare/onboarding/intro-popup';
import { ProjectCapabilitiesProvider } from '@townsquare/project-capabilities';
import { SIDEBAR_PANEL } from '@townsquare/sidebar-panel/panels';
import { usePreloadedEntryPointPanel } from '@townsquare/sidebar-panel-utils/hooks/use-preloaded-entry-point-panel';
import { SiteBannerProvider } from '@townsquare/site-banner';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { ProductHome } from '../griffin-navigation/ProductHome';
import { useSideNavAppItemsForNavPlatform } from '../griffin-navigation/use-side-nav-app-items';
import { useSideNavItems } from '../griffin-navigation/use-side-nav-items';
import { useUniversalCreateItems } from '../griffin-navigation/use-universal-create-items';
import { GriffinLayout } from '../layouts/Home/GriffinLayout';

export function GriffinRoute(): React.JSX.Element {
	return (
		<Root isSideNavShortcutEnabled={fg('navx-full-height-sidebar')}>
			<SiteBannerProvider>
				<GlobalAppShortcutsProvider>
					<GriffinRouteComponent />
				</GlobalAppShortcutsProvider>
			</SiteBannerProvider>
		</Root>
	);
}

const onBlanketClicked = () => {
	document.dispatchEvent(new Event(DISMISS_SPOTLIGHT_TOUR_EVENT_NAME));
};

const GriffinTopSection = () => {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const { items: universalCreateItems, compact } = useUniversalCreateItems();

	return (
		<GriffinTopNavigation
			renderProductHome={() => (
				<AppHomeWrapper>
					<ProductHome />
				</AppHomeWrapper>
			)}
			universalCreateItems={universalCreateItems}
			compact={compact}
			hideSideNavToggleButton={noWorkspaceExperience}
		/>
	);
};

function GriffinRouteComponent() {
	const noWorkspaceExperience = useNoWorkspaceExperience();
	const sidebarItems = useSideNavItems();

	const hideSideNav = noWorkspaceExperience;

	const preloadedPanel = usePreloadedEntryPointPanel();
	const { Panel: SidebarPanel, Provider: SidebarPanelContext } = usePanelContext(
		SIDEBAR_PANEL,
		preloadedPanel,
	);

	return (
		<>
			<SpotlightManager onBlanketClicked={onBlanketClicked} blanketIsTinted={false}>
				<SidebarPanelContext>
					<GoalCapabilitiesProvider>
						<ProjectCapabilitiesProvider>
							<GriffinTopSection />
						</ProjectCapabilitiesProvider>
					</GoalCapabilitiesProvider>
					{!hideSideNav && <PlatformSideNav sidebarItems={sidebarItems} />}
					<GriffinLayout PlatformSidebarPanel={SidebarPanel} />
				</SidebarPanelContext>
			</SpotlightManager>
			{noWorkspaceExperience && <IntroPopup />}
		</>
	);
}

function PlatformSideNav({ sidebarItems }: { sidebarItems: SideNavItem[] }) {
	const appShortcuts = useSideNavAppItemsForNavPlatform();
	const { formatMessage } = useIntl();

	const disableMenuCustomization = true;

	return (
		<NavPlatformSideNav
			app="projects"
			appLabel={formatMessage(projectsAppLabelMessage)}
			appItems={sidebarItems}
			appShortcuts={appShortcuts}
			customizationStore="local"
			disableMenuCustomization={disableMenuCustomization}
		/>
	);
}

const projectsAppLabelMessage = defineMessage({
	id: 'townsquare.projects.app.label',
	defaultMessage: 'Projects',
	description: 'Label for the app when shown in the global side navigation.',
});
